import React, { useEffect, useMemo, useRef, useState } from "react";

export interface Option { value: string; label: string }

/** select2-equivalent searchable dropdown (legacy `.drpSelect`) */
export const ErpSelect: React.FC<{
  id: string; value: string; options: Option[]; onChange: (v: string, opt?: Option) => void;
  placeholder?: string; invalid?: boolean; disabled?: boolean; testId?: string;
}> = ({ id, value, options, onChange, placeholder = " Select", invalid, disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const selected = options.find((o) => o.value === value);
  const filtered = useMemo(() => options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())), [options, q]);
  return (
    <div className="erp-select" ref={ref}>
      <button type="button" id={id} disabled={disabled} data-testid={testId || id}
        className={`form-select text-start ${invalid ? "erp-form-error" : ""}`} onClick={() => { setOpen((o) => !o); setQ(""); }}>
        {selected ? selected.label : <span className="text-muted">{placeholder}</span>}
      </button>
      {open && (
        <div className="erp-select-menu" data-testid={`${testId || id}-menu`}>
          <input autoFocus className="form-control form-control-sm" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId || id}-search`} />
          <div onClick={() => { onChange("", undefined); setOpen(false); }}>{placeholder}</div>
          {filtered.map((o) => (
            <div key={o.value} className={o.value === value ? "active" : ""} data-testid={`${testId || id}-option-${o.value}`}
              onClick={() => { onChange(o.value, o); setOpen(false); }}>{o.label}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Field: React.FC<{ label: React.ReactNode; required?: boolean; col?: string; htmlFor?: string; children: React.ReactNode }> =
  ({ label, required, col = "col-md-6 col-lg-3 mb-3", htmlFor, children }) => (
    <div className={`form-group ${col}`}>
      <label htmlFor={htmlFor} className="form-label">{label}{required && <span className="erp-required">*</span>}</label>
      {children}
    </div>
  );

export const Alert: React.FC<{ kind: "success" | "danger" | "warning" | "info"; children: React.ReactNode; testId?: string }> = ({ kind, children, testId }) => (
  <div className={`alert alert-${kind} erp-alert-inline`} role="alert" data-testid={testId}>{children}</div>
);

export interface Column<T> { key: string; title: string; render?: (row: T, idx: number) => React.ReactNode; className?: string }

/** Dense bordered table = legacy `#tblDataTable.table.table-hover.table-bordered` */
export function ErpTable<T extends Record<string, any>>({ columns, rows, loading, error, footer, testId = "data-table", emptyText = "No data available in table" }:
  { columns: Column<T>[]; rows: T[]; loading?: boolean; error?: string | null; footer?: React.ReactNode; testId?: string; emptyText?: string }) {
  return (
    <div className="erp-table-wrap" data-testid={testId}>
      <table className="table table-hover table-bordered table-sm">
        <thead className="thead-light"><tr>{columns.map((c) => <th key={c.key} className={c.className}>{c.title}</th>)}</tr></thead>
        <tbody>
          {loading && <tr><td colSpan={columns.length} className="text-center"><span className="erp-spinner" /> Loading…</td></tr>}
          {!loading && error && <tr><td colSpan={columns.length} className="text-danger" data-testid={`${testId}-error`}>{error}</td></tr>}
          {!loading && !error && rows.length === 0 && <tr><td colSpan={columns.length} className="text-center text-muted">{emptyText}</td></tr>}
          {!loading && !error && rows.map((r, i) => (
            <tr key={i} data-testid={`${testId}-row-${i}`}>{columns.map((c) => <td key={c.key} className={c.className}>{c.render ? c.render(r, i) : String(r[c.key] ?? "")}</td>)}</tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
}

export const Modal: React.FC<{ open: boolean; title: string; onClose: () => void; children: React.ReactNode; testId?: string }> = ({ open, title, onClose, children, testId }) => {
  if (!open) return null;
  return (
    <>
      <div className="erp-modal-backdrop" onClick={onClose} />
      <div className="erp-modal" data-testid={testId}>
        <div className="modal-content">
          <div className="modal-header"><h5 className="modal-title">{title}</h5><button type="button" className="btn-close" aria-label="Close" onClick={onClose} data-testid={`${testId}-close`} /></div>
          <div className="modal-body">{children}</div>
        </div>
      </div>
    </>
  );
};

export const today = () => new Date().toISOString().slice(0, 10);
export const toOptions = <T,>(rows: T[] | undefined, value: (r: T) => unknown, label: (r: T) => unknown): Option[] =>
  (rows || []).map((r) => ({ value: String(value(r) ?? ""), label: String(label(r) ?? "") }));
